import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '@/types/filters.types';
import { listFacilities, listPlanTypes, listUsers, type AdminUser } from '@/services/admin.service';
import { listQuoteItems, listPaymentMethodOptions } from '@/services/payments.service';
import { listTemplates } from '@/services/memberships.service';
import { PAYMENT_STATUS_OPTIONS } from '@/pages/dashboard/dashboardFilters';
import { PAYMENT_SOURCE_OPTIONS } from '@/lib/filterRegistry';
import { useLeadSourceOptions } from '@/hooks/filters/leadSourceOptions';

export function usePaymentFilterOptions(opts?: { enabled?: boolean }): Record<string, FilterOption[]> {
  const enabled = opts?.enabled ?? true;

  // Referral Source is the payer's — the same category/source tree the
  // Patients list shows, built by the shared hierarchy hook.
  const leadSource = useLeadSourceOptions({ enabled });

  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => listFacilities(),
    staleTime: 60_000,
    enabled,
  });

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: quoteItems } = useQuery({
    queryKey: ['quote-items'],
    queryFn: () => listQuoteItems(),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: planTypes } = useQuery({
    queryKey: ['plan-types'],
    queryFn: listPlanTypes,
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: membershipTemplates } = useQuery({
    queryKey: ['membership-templates-filter'],
    queryFn: () => listTemplates({ incrementType: 1, limit: 500 }),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: packageTemplates } = useQuery({
    queryKey: ['package-templates-filter'],
    queryFn: () => listTemplates({ incrementType: 2, limit: 500 }),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    staleTime: 5 * 60_000,
    enabled,
  });

  return useMemo<Record<string, FilterOption[]>>(() => {
    const doctors = (users ?? [])
      .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('doctor'))
      .map((u: AdminUser) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' }));

    const coordinators = (users ?? [])
      .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('coordinator'))
      .map((u: AdminUser) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' }));

    return {
      'payment-type': [
        ...(paymentMethods ?? [])
          .filter((m) => !m.name.toLowerCase().includes('gift'))
          .map((m) => ({ value: String(m.paymentMethodID), label: m.name })),
        { value: 'gift_card', label: 'Gift Card' },
        { value: 'comp', label: 'Comp' },
      ],
      facility: (facilities ?? []).filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
      'payment-status': PAYMENT_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      // Static: the buckets are defined in SQL, not fetched from a table.
      'payment-source': PAYMENT_SOURCE_OPTIONS,
      doctor: doctors,
      coordinator: coordinators,
      product: [...(quoteItems ?? [])]
        .filter((qi) => !qi.name.includes('----'))
        .sort((a, b) => {
          const catA = (a.categoryTitle ?? 'Other').toLowerCase();
          const catB = (b.categoryTitle ?? 'Other').toLowerCase();
          if (catA !== catB) return catA.localeCompare(catB);
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        })
        .map((qi) => ({ value: String(qi.itemId), label: qi.name, group: qi.categoryTitle ?? 'Other' })),
      'plan-type': (planTypes ?? []).filter((pt) => pt.active).map((pt) => ({ value: String(pt.planTypeId), label: pt.name })),
      'membership-type': (membershipTemplates ?? []).filter((t) => !t.title.includes('----')).map((t) => ({ value: String(t.templateId), label: t.title })),
      'package-type': (packageTemplates ?? []).filter((t) => !t.title.includes('----')).map((t) => ({ value: String(t.templateId), label: t.title })),
      users: (users ?? []).map((u) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}` })),
      'lead-source': leadSource,
    };
  }, [facilities, users, quoteItems, planTypes, membershipTemplates, packageTemplates, paymentMethods, leadSource]);
}
